import {
  Card,
  CardBody,
  Col,
  Row,
  InputGroup,
  Input,
  Label,
  Form,
  FormGroup,
  InputGroupText,
  ModalBody,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { H5, Image, P, Btn } from "../../../../../AbstractElements";
import CommonCardHeader from "../../../../CommonComponents/CommonCardHeader/CommonCardHeader";
import { Link } from "react-router-dom";
import { Save } from "react-feather";
import Swal from "sweetalert2";

// Modals
import UpdateEmpInfoModal from "../../../../UiKits/Modal/Employee/UpdateEmploymentInfo";

// Services
import {
  employeeService,
  VIEW_EMPLOYEE_PROFILE_QUERY_KEY,
} from "../../../../../Services/employeeService";

import {
  VIEW_EMPTYPE_QUERY_KEY,
  employmentTypeService,
} from "../../../../../Services/employmentTypesServices";

import {
  VIEW_EMPSTATUS_QUERY_KEY,
  employeeStatusService,
} from "../../../../../Services/employeeStatusServices";

import {
  VIEW_EMPTIMELINE_QUERY_KEY,
  employmentHistoryService,
} from "../../../../../Services/employmentHistoryServices";

import {
  joblevelService,
  VIEW_JOBLEVEL_QUERY_KEY,
} from "../../../../../Services/joblevelService";

// Type
import { ViewEmployeeStatus } from "../../../../../Types/MasterData/EmployeeStatus";
import { ViewJobLevel } from "../../../../../Types/MasterData/JobLevel";
import { ViewEmploymentType } from "../../../../../Types/MasterData/EmploymentTypes";
import { ViewEmployeeProfile } from "../../../../../Types/Employee/EmployeeModel";
import { CreateEmploymentHistory } from "../../../../../Types/Employee/EmploymentHistory";
import {
  jobtitleService,
  VIEW_JOBTITLES_QUERY_KEY,
} from "../../../../../Services/jobtitlesService";
import { ViewJobTitle } from "../../../../../Types/MasterData/JobTitles";

interface EmployeeProfileProps {
  EmpNo: string;
  toggle?: () => void;
}

const CreateEmpMovementForm: React.FC<EmployeeProfileProps> = ({
  toggle,
  EmpNo,
}) => {
  const INITIAL_STATE = {
    emp_no: EmpNo,
    movement_type: "000",
    employee_status: "",
    employment_type: "",
    job_level: 0,
    job_title_id: "",
    effectivity_date: "",
    remarks: "",
    basic_salary: 0,
    salary_type: "",
    pay_frequency: "",
  };

  const [formData, setFormData] = useState(INITIAL_STATE);
  const [profileData, setProfileData] = useState<ViewEmployeeProfile | null>(
    null,
  );

  // Get Employee Profile
  const { data: empProfileResponse, isLoading: empProfileIsFetching } =
    useQuery({
      queryKey: [VIEW_EMPLOYEE_PROFILE_QUERY_KEY, EmpNo],
      queryFn: () => employeeService.viewEmployeeProfileByEmpno(EmpNo),
      enabled: !!EmpNo,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

  useEffect(() => {
    const profile = empProfileResponse?.data;

    if (!profile) return;

    setProfileData(profile);

    setFormData((prev) => ({
      ...prev,
      job_level: profile.job_level ?? prev.job_level,

      basic_salary: profile.basic_salary ?? prev.basic_salary,

      salary_type: profile.salary_type ?? prev.salary_type,

      pay_frequency: profile.pay_frequency ?? prev.pay_frequency,
    }));
  }, [empProfileResponse?.data]);

  const queryClient = useQueryClient();

  // Get Job Titles
  const { data: jobTitlesResponse, isLoading: jobTitlesIsLoading } = useQuery<{
    data: ViewJobTitle[];
  }>({
    queryKey: VIEW_JOBTITLES_QUERY_KEY,
    queryFn: jobtitleService.viewAllJobTitle,
    enabled:
      formData.movement_type === "JOBC" || formData.movement_type === "INIT",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const jobTitleData: ViewJobTitle[] =
    jobTitlesResponse?.data?.filter((item) => item.status === 1) ?? [];

  // Get Employee Status
  const { data: empStatusResponse, isLoading: empStatusIsLoading } = useQuery<{
    data: ViewEmployeeStatus[];
  }>({
    queryKey: VIEW_EMPSTATUS_QUERY_KEY,
    queryFn: employeeStatusService.viewAllEmployeeStatus,
    enabled: formData.movement_type === "STAT",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const empStatusData: ViewEmployeeStatus[] =
    empStatusResponse?.data?.filter((item) => item.status === 1) ?? [];

  // Get Employee Types
  const { data: empTypeResponse, isLoading: empTypeIsLoading } = useQuery<{
    data: ViewEmployeeStatus[];
  }>({
    queryKey: VIEW_EMPTYPE_QUERY_KEY,
    queryFn: employmentTypeService.viewAllEmploymentType,
    enabled: formData.movement_type === "TYPE",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const empTypeData: ViewEmploymentType[] =
    empTypeResponse?.data?.filter((types) => types.status === 1) ?? [];

  // Get Job Level
  const { data: JobLvlResponse, isLoading: JobLevelIsLoading } = useQuery<{
    data: ViewJobLevel[];
  }>({
    queryKey: VIEW_JOBLEVEL_QUERY_KEY,
    queryFn: joblevelService.viewAllJobLevel,
    enabled:
      formData.movement_type === "PROM" || formData.movement_type === "INIT",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const JobLevelData: ViewJobLevel[] =
    JobLvlResponse?.data?.filter((types) => types.status === 1) ?? [];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateEmploymentHistory) =>
      employmentHistoryService.createEmploymentHistory(data),

    onMutate: () => {
      Swal.fire({
        title: "Processing Request",
        text: "The system is prcoessing the transaction. Please wait...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "Employee record has been updated successfully.",
        confirmButtonColor: "#4e73df",
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Refreshing Employee Information",
            text: "The system is refreshing the updated employee information. Please wait...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();

              setTimeout(() => {
                Swal.close();
              }, 2000);
            },
          });
        }
      });

      if (toggle) toggle();
    },

    onError: (error: any) => {
      const responseData = error.response?.data;

      Swal.close();

      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: true,
        confirmButtonText: "OK",
        icon: "error",
        title: "Error",
        text: responseData?.message || "Failed to update employment history.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let confirmationTitle = "";
    let main_icon = "";
    let color = "";
    let tag = "";
    let main_title = "";
    let description = "";
    let sub_icon = "";
    let sub_title = "";
    let remarks = "";
    let new_effectivity_date = "";
    let new_hired_date = "";
    let new_start_date = "";
    let new_end_date = "";
    let new_job_title = "";
    let new_job_level = "";
    let new_basic_salary = 0;
    let new_salary_type = "";
    let new_pay_frequency = "";

    const employeeName = `${profileData?.first_name ?? ""} ${
      profileData?.last_name ?? ""
    }`.trim();

    const effectivityDate = formData.effectivity_date;

    if (formData.movement_type === "STAT") {
      // ============================================================
      // CHANGE EMPLOYMENT STATUS
      // ============================================================

      const selectedEmployeeStatus = empStatusData.find(
        (item) => String(item.id) === String(formData.employee_status),
      );

      const employeeStatusLabel = selectedEmployeeStatus?.name ?? "";

      confirmationTitle = "Change Employment Status?";
      main_icon = "icon-alert";
      sub_icon = "";
      sub_title = "";
      remarks = formData.remarks;

      if (["1", "2", "3"].includes(String(formData.employee_status))) {
        color = "warning";
        tag = "EMPLOYMENT STATUS";
        main_title = "Employment Status Changed";
      } else {
        color = "danger";
        tag = employeeStatusLabel.toUpperCase();
        main_title = `Employee is ${employeeStatusLabel}`;

        new_end_date = effectivityDate;
      }

      description = `${employeeName}'s employment status was changed to ${employeeStatusLabel}.`;
    } else if (formData.movement_type === "TYPE") {
      // ============================================================
      // CHANGE EMPLOYMENT TYPE
      // ============================================================

      const selectedEmployeeType = empTypeData.find(
        (item) => String(item.id) === String(formData.employment_type),
      );

      const employeeTypeLabel = selectedEmployeeType?.name ?? "";

      confirmationTitle = "Change Employment Type?";
      new_effectivity_date = effectivityDate;

      sub_icon = "";
      sub_title = "";
      remarks = formData.remarks;

      if (String(formData.employment_type) === "1") {
        main_icon = "icon-cup";
        color = "success";
        tag = "REGULARIZATION";
        main_title = "Employee was regularized.";
      } else {
        main_icon = "icon-bookmark-alt";
        color = "warning";
        tag = "EMPLOYMENT TYPE";
        main_title = "Employment Type Changed";
      }

      description = `${employeeName}'s employment type was changed to ${employeeTypeLabel}.`;
    } else if (
      formData.movement_type === "PROM" ||
      formData.movement_type === "DEMO"
    ) {
      // ============================================================
      // PROMOTION / DEMOTION
      // ============================================================

      new_job_level = String(formData.job_level);
      const selectedRank = JobLevelData.find(
        (item) => String(item.id) === String(formData.job_level),
      );

      const rankLabel = selectedRank
        ? `LEVEL ${selectedRank.level_rank} - ${selectedRank.name.toUpperCase()}`
        : "* No Data Found *";

      const isPromotion = formData.movement_type === "PROM";

      confirmationTitle = isPromotion
        ? "Promote Employee?"
        : "Demote Employee?";

      main_icon = isPromotion ? "icon-cup" : "icon-alert";
      color = isPromotion ? "success" : "secondary";
      tag = isPromotion ? "PROMOTION" : "DEMOTION";
      main_title = isPromotion
        ? "Employee was promoted."
        : "Employee was demoted.";

      sub_icon = "";
      sub_title = "";

      remarks = formData.remarks;

      description = `${employeeName}'s Job Level / Rank was changed to ${rankLabel}.`;
    } else if (formData.movement_type === "SALR") {
      // ============================================================
      // SALARY ADJUSTMENT
      // ============================================================

      confirmationTitle = "Adjust Employee Salary?";

      new_effectivity_date = effectivityDate;
      new_basic_salary = formData.basic_salary;
      new_salary_type = formData.salary_type;
      new_pay_frequency = formData.pay_frequency;

      // ------------------------------------------------------------
      // Current / New Salary
      // ------------------------------------------------------------
      const currentSalary = Number(profileData?.basic_salary || 0);
      const newSalary = Number(formData.basic_salary || 0);

      const isIncrease = newSalary > currentSalary;
      const isDecrease = newSalary < currentSalary;
      const isSame = newSalary === currentSalary;

      // ------------------------------------------------------------
      // Salary Type / Pay Frequency Labels
      // ------------------------------------------------------------
      const salaryTypeLabels: Record<string, string> = {
        MNTH: "Monthly",
        SMNT: "Semi-Monthly",
        WEEK: "Weekly",
        DAIL: "Daily",
        HOUR: "Hourly",
      };

      const payFrequencyLabels: Record<string, string> = {
        MNTH: "Monthly",
        SMNT: "Semi-Monthly",
        WEEK: "Weekly",
        DAIL: "Daily",
        HOUR: "Hourly",
      };

      const salaryTypeLabel =
        salaryTypeLabels[new_salary_type] || profileData?.salary_type || "-";

      const payFrequencyLabel =
        payFrequencyLabels[new_pay_frequency] ||
        profileData?.pay_frequency ||
        "-";

      // ------------------------------------------------------------
      // Salary Formatting
      // ------------------------------------------------------------
      const formatSalary = (
        salary: number | string | null | undefined,
      ): string =>
        Number(salary || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      const currentSalaryFormatted = formatSalary(currentSalary);
      const newSalaryFormatted = formatSalary(newSalary);

      // ------------------------------------------------------------
      // Salary Adjustment Timeline
      // ------------------------------------------------------------
      if (isIncrease && newSalary > 0) {
        main_icon = "icon-cup";
        color = "success";
        tag = "SALARY INCREASE";
        main_title = "Employee Salary Increased";

        description = `${employeeName}'s salary information was decreased. Salary Type: ${salaryTypeLabel} | Pay Frequency: ${payFrequencyLabel}`;

        sub_icon = "icon-envelope";
        sub_title = `New Salary: ${newSalaryFormatted}`;
      } else if (isDecrease && newSalary > 0) {
        main_icon = "icon-stats-down";
        color = "secondary";
        tag = "SALARY DECREASE";
        main_title = "Employee Salary Decreased";

        description = `${employeeName}'s salary information was decreased. Updated Information: Salary Type: ${salaryTypeLabel} | Pay Frequency: ${payFrequencyLabel}`;

        sub_icon = "icon-envelope";
        sub_title = `New Salary: ${newSalaryFormatted}`;
      } else {
        main_icon = "icon-info-alt";
        color = "warning";
        tag = "SALARY ADJUSTMENT";
        main_title = "Employee Salary Information Adjusted";

        description =
          `${employeeName}'s salary information was adjusted. ` +
          `Salary: ${currentSalaryFormatted} | ` +
          `Salary Type: ${salaryTypeLabel} | ` +
          `Pay Frequency: ${payFrequencyLabel}`;

        sub_icon = "";
        sub_title = "";
      }

      remarks = formData.remarks;
    } else if (formData.movement_type === "JOBC") {
      // ============================================================
      // JOB TITLE / POSITION CHANGE
      // ============================================================

      const selectedJobTitle = jobTitleData.find(
        (item) => String(item.id) === String(formData.job_title_id),
      );

      const newJobTitle = selectedJobTitle?.name ?? "-";

      confirmationTitle = "Change Employee's Job Position?";

      // New employment information
      new_job_title = formData.job_title_id;
      new_effectivity_date = effectivityDate;

      // Timeline
      main_title = "Job Position Changed";
      main_icon = "icon-briefcase";
      color = "info";
      tag = "NEW POSITION";

      description = `${employeeName}'s job position was updated. `;

      sub_icon = "icon-briefcase";
      sub_title = newJobTitle.toUpperCase();

      remarks = formData.remarks;
    } else if (formData.movement_type === "INIT") {
      // ============================================================
      // INITIAL EMPLOYMENT DETAILS
      // ============================================================
      confirmationTitle = "Adjust Employee Salary?";
      new_job_level = String(formData.job_level);
      const selectedRank = JobLevelData.find(
        (item) => String(item.id) === String(formData.job_level),
      );
      const rankLabel = selectedRank
        ? `LEVEL ${selectedRank.level_rank} - ${selectedRank.name.toUpperCase()}`
        : "* No Data Found *";

      const selectedJobTitle = jobTitleData.find(
        (item) => String(item.id) === String(formData.job_title_id),
      );

      const newJobTitle = selectedJobTitle?.name ?? "-";

      new_effectivity_date = effectivityDate;
      new_basic_salary = formData.basic_salary;
      new_salary_type = formData.salary_type;
      new_pay_frequency = formData.pay_frequency;
      new_job_title = formData.job_title_id;

      // ------------------------------------------------------------
      // Current / New Salary
      // ------------------------------------------------------------
      const newSalary = Number(formData.basic_salary || 0);

      // ------------------------------------------------------------
      // Salary Type / Pay Frequency Labels
      // ------------------------------------------------------------
      const salaryTypeLabels: Record<string, string> = {
        MNTH: "Monthly",
        SMNT: "Semi-Monthly",
        WEEK: "Weekly",
        DAIL: "Daily",
        HOUR: "Hourly",
      };

      const payFrequencyLabels: Record<string, string> = {
        MNTH: "Monthly",
        SMNT: "Semi-Monthly",
        WEEK: "Weekly",
        DAIL: "Daily",
        HOUR: "Hourly",
      };

      const salaryTypeLabel =
        salaryTypeLabels[new_salary_type] || profileData?.salary_type || "-";

      const payFrequencyLabel =
        payFrequencyLabels[new_pay_frequency] ||
        profileData?.pay_frequency ||
        "-";

      // ------------------------------------------------------------
      // Salary Formatting
      // ------------------------------------------------------------
      const formatSalary = (
        salary: number | string | null | undefined,
      ): string =>
        Number(salary || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      const newSalaryFormatted = formatSalary(newSalary);

      confirmationTitle = "Save Initial Details";
      new_effectivity_date = effectivityDate;

      main_icon = "icon-info-alt";
      color = "info";
      tag = "INITIAL DETAILS";
      main_title = "Setup Initial Employment Details";

      description =
        `${employeeName}'s initial employment details has been setuped. ` +
        `Salary: ${newSalaryFormatted} | ` +
        `Salary Type: ${salaryTypeLabel} | ` +
        `Pay Frequency: ${payFrequencyLabel} `;

      sub_icon = "icon-briefcase";
      sub_title =
        newJobTitle.toUpperCase() + ` (` + rankLabel.toUpperCase() + `)`;
    }
    Swal.fire({
      title: confirmationTitle,
      text: 'You are about to perform a CRITICAL action. This cannot be undone. Please type "Confirm" to proceed.',
      icon: "warning",

      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-save"></i> Save Adjustment',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',

      confirmButtonColor: "#4e73df",
      cancelButtonColor: "#dc3545",

      allowOutsideClick: false,
      allowEscapeKey: false,

      input: "text",
      inputValue: "",
      inputPlaceholder: 'Type "Confirm" to continue',
      inputAutoFocus: true,

      didOpen: () => {
        const input = Swal.getInput();

        if (input) {
          input.value = "";
          input.setAttribute("autocomplete", "off");
        }
      },

      inputValidator: (value) => {
        if (value.trim() !== "Confirm") {
          return 'Please type "Confirm" to proceed.';
        }

        return null;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Saving...",
          text: "Please wait while your activity is being saved.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => Swal.showLoading(),
        });

        const payload: CreateEmploymentHistory = {
          emp_no: EmpNo,
          new_employment_status: formData.employee_status || "",
          new_employment_type: formData.employment_type || "",
          new_effectivity_date: new_effectivity_date || "",
          new_hired_date: new_hired_date || "",
          new_start_date: new_start_date || "",
          new_end_date: new_end_date || "",
          new_job_title: new_job_title || "",
          new_job_level: new_job_level || "",
          new_basic_salary: new_basic_salary || 0,
          new_salary_type: new_salary_type || "",
          new_pay_frequency: new_pay_frequency || "",
          effectivity_date: formData.effectivity_date || "",
          main_icon: main_icon || "",
          color: color || "",
          tag: tag || "",
          main_title: main_title || "",
          description: description || "",
          sub_icon: sub_icon || "",
          sub_title: sub_title || "",
          remarks: remarks || "",
        };

        createMutation.mutate(payload);
      }
    });
  };

  return (
    <ModalBody className="large-modal-body modal-body">
      <Form className="needs-validation w-100" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <Row className="p-1">
              <Col md="12">
                <FormGroup>
                  <Label>
                    Employment Type <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-atom txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="movement_type"
                      name="movement_type"
                      required
                      onChange={handleInputChange}
                    >
                      <option value="000" disabled selected>
                        SELECT MOVEMENT
                      </option>
                      <option value="STAT">CHANGE EMPLOYMENT STATUS</option>
                      <option value="TYPE">CHANGE EMPLOYMENT TYPE</option>
                      <option value="PROM">EMPLOYEE PROMOTION</option>
                      {/* Job Rank Up, Effectivity Date */}

                      <option value="DEMO">EMPLOYEE DEMOTION</option>
                      {/* Job Rank Down, Effectivity Date */}

                      <option value="SALR">SALARY ADJUSTMENT</option>
                      {/* Basic Salary, Salary Type, Pay Frequency */}

                      <option value="JOBC">JOB TITLE CHANGE</option>
                      {/* Job Title, Effectivity Date */}

                      <option value="INIT">INITIAL EMPLOYMENT DETAILS</option>
                      {/*Basic Salary, Job Level, Salary Type, Pay Frequency, effectivity date*/}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6" hidden={formData.movement_type !== "STAT"}>
                <FormGroup>
                  <Label>
                    Employee Status <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="employee_status"
                      name="employee_status"
                      disabled={formData.movement_type !== "STAT"}
                      value={formData.employee_status}
                      required={formData.movement_type === "STAT"}
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>
                        {empStatusIsLoading
                          ? "Loading..."
                          : "SELECT EMPLOYEE STATUS"}
                      </option>

                      {!empStatusIsLoading &&
                        empStatusData.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col
                md="6"
                hidden={
                  formData.movement_type !== "JOBC" &&
                  formData.movement_type !== "INIT"
                }
              >
                <FormGroup>
                  <Label>
                    Job Title <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="job_title_id"
                      name="job_title_id"
                      disabled={
                        formData.movement_type !== "JOBC" &&
                        formData.movement_type !== "INIT"
                      }
                      value={formData.job_title_id}
                      required={
                        formData.movement_type === "JOBC" ||
                        formData.movement_type === "INIT"
                      }
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>
                        {jobTitlesIsLoading ? "Loading..." : "SELECT JOB TITLE"}
                      </option>

                      {!jobTitlesIsLoading &&
                        jobTitleData.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name?.toUpperCase()}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col
                md="6"
                hidden={
                  formData.movement_type !== "PROM" &&
                  formData.movement_type !== "DEMO" &&
                  formData.movement_type !== "INIT"
                }
              >
                <FormGroup>
                  <Label>
                    Job Rank / Level <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="job_level"
                      name="job_level"
                      disabled={
                        formData.movement_type !== "PROM" &&
                        formData.movement_type !== "DEMO" &&
                        formData.movement_type !== "INIT"
                      }
                      value={formData?.job_level}
                      required={
                        formData.movement_type === "PROM" ||
                        formData.movement_type === "DEMO" ||
                        formData.movement_type !== "INIT"
                      }
                      onChange={handleInputChange}
                    >
                      <option value={0} disabled selected>
                        {JobLevelIsLoading ? "Loading..." : "Select Job Level"}
                      </option>

                      {!JobLevelIsLoading &&
                        (() => {
                          const filteredLevels =
                            formData.movement_type === "PROM"
                              ? JobLevelData.filter(
                                  (item) =>
                                    profileData?.job_level != null &&
                                    item.level_rank <= profileData.job_level,
                                )
                              : formData.movement_type === "DEMO"
                                ? JobLevelData.filter(
                                    (item) =>
                                      profileData?.job_level != null &&
                                      item.level_rank >= profileData.job_level,
                                  )
                                : JobLevelData;

                          return filteredLevels.length > 0 ? (
                            filteredLevels.map((item) => (
                              <option key={item.id} value={item.id}>
                                {"LEVEL " +
                                  item.level_rank +
                                  " - " +
                                  item.name.toUpperCase()}
                              </option>
                            ))
                          ) : (
                            <option disabled>* No Data Found *</option>
                          );
                        })()}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6" hidden={formData.movement_type !== "TYPE"}>
                <FormGroup>
                  <Label>
                    Employment Type <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="employment_type"
                      name="employment_type"
                      disabled={formData.movement_type !== "TYPE"}
                      value={formData.employment_type}
                      required={formData.movement_type === "TYPE"}
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>
                        {empTypeIsLoading
                          ? "Loading..."
                          : "SELECT EMPLOYMENT TYPE"}
                      </option>

                      {!empTypeIsLoading &&
                        empTypeData.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col
                md="6"
                hidden={
                  formData.movement_type !== "SALR" &&
                  formData.movement_type !== "INIT"
                }
              >
                <FormGroup>
                  <Label>
                    Basic Salary <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-cur-peso txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="number"
                      id="basic_salary"
                      name="basic_salary"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="text-end"
                      value={formData.basic_salary ?? ""}
                      disabled={
                        formData.movement_type !== "SALR" &&
                        formData.movement_type !== "INIT"
                      }
                      required={
                        formData.movement_type === "SALR" ||
                        formData.movement_type === "INIT"
                      }
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col
                md="6"
                hidden={
                  formData.movement_type !== "SALR" &&
                  formData.movement_type !== "INIT"
                }
              >
                <FormGroup>
                  <Label>
                    Salary Type <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="salary_type"
                      name="salary_type"
                      disabled={
                        formData.movement_type !== "SALR" &&
                        formData.movement_type !== "INIT"
                      }
                      required={
                        formData.movement_type === "SALR" ||
                        formData.movement_type === "INIT"
                      }
                      onChange={handleInputChange}
                    >
                      <option value="" selected>
                        Select Salary Type
                      </option>
                      <option value="MNTH">Monthly</option>
                      <option value="SMNT">Semi-Monthly</option>
                      <option value="WEEK">Weekly</option>
                      <option value="DAYS">Daily</option>
                      <option value="HOUR">Hourly</option>
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col
                md="6"
                hidden={
                  formData.movement_type !== "SALR" &&
                  formData.movement_type !== "INIT"
                }
              >
                <FormGroup>
                  <Label>
                    Pay Frequency <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="pay_frequency"
                      name="pay_frequency"
                      disabled={
                        formData.movement_type !== "SALR" &&
                        formData.movement_type !== "INIT"
                      }
                      required={
                        formData.movement_type === "SALR" ||
                        formData.movement_type === "INIT"
                      }
                      onChange={handleInputChange}
                    >
                      <option value="" selected>
                        Select Pay Frequency
                      </option>
                      <option value="MNTH">Monthly</option>
                      <option value="SMNT">Semi-Monthly</option>
                      <option value="WEEK">Weekly</option>
                      <option value="DAIL">Daily</option>
                      <option value="HOUR">Hourly</option>
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col sm="6" hidden={formData.movement_type === "000"}>
                <FormGroup>
                  <Label>
                    Effectivity Date <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="date"
                      id="effectivity_date"
                      name="effectivity_date"
                      onChange={handleInputChange}
                      required
                      min={profileData?.hired_date?.replace(/\//g, "-")}
                      max={profileData?.end_date?.replace(/\//g, "-")}
                      value={formData?.effectivity_date || ""}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col sm="12" hidden={formData.movement_type === "000"}>
                <FormGroup>
                  <Label>Remarks</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-pen-alt-2 txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      id="remarks"
                      name="remarks"
                      onChange={handleInputChange}
                      max={150}
                      value={formData?.remarks || ""}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <div
                className="d-flex justify-content-end"
                hidden={formData.movement_type === "000"}
              >
                <Col md="4">
                  <Btn
                    color="primary"
                    className="w-100"
                    type="submit"
                    // disabled={updateMutation.isPending}
                  >
                    <Save size={16} className="me-1" />
                    {/* {updateMutation.isPending ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Updating...
                    </>
                  ) : ( */}
                    Save
                    {/* )} */}
                  </Btn>
                </Col>
              </div>
            </Row>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateEmpMovementForm;
